import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, X, ChevronDown, Clock, BookOpen, Sparkles, ArrowRight, ShieldCheck } from 'lucide-react';
const MOCKUP_URL = 'https://cdn.magicpatterns.com/uploads/8KfuaVEcRaADopibQqNbNj/heylloyd-image-1781370767812.png';
const FadeIn = ({
  children,
  delay = 0
}: {
  children: React.ReactNode;
  delay?: number;
}) => <motion.div initial={{
  opacity: 0,
  y: 20
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-100px'
}} transition={{
  duration: 0.7,
  delay,
  ease: 'easeOut'
}} data-id="element-0">
    {children}
  </motion.div>;
export function App() {
  const [activeModal, setActiveModal] = useState<'privacy' | 'terms' | null>(null);
  return <div className="font-sans text-sage-900 bg-ivory min-h-screen selection:bg-sage-200 selection:text-sage-900" data-id="element-1">
      {/* HERO SECTION */}
      <section className="relative pt-20 pb-24 lg:pt-32 lg:pb-32 overflow-hidden" data-id="element-2">
        <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-3">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-8 items-center" data-id="element-4">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.8,
            ease: 'easeOut'
          }} className="max-w-2xl" data-id="element-5">
              <span className="inline-block py-1 px-3 rounded-full bg-sage-200/50 text-sage-900 text-sm font-medium tracking-wide mb-6" data-id="element-6">
                El Protocolo del Plato
              </span>
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.1] text-sage-900 mb-6" data-id="element-7">
                "Poder Perimenopáusico" en 15 Minutos
              </h1>
              <p className="text-lg sm:text-xl text-sage-900/80 leading-relaxed mb-8 font-light" data-id="element-8">
                Detén la hinchazón abdominal "misteriosa" y recupera tu energía
                sin dietas restrictivas ni horas en el gimnasio.
              </p>

              <div className="prose prose-sage text-sage-900/80 mb-10 font-light leading-relaxed" data-id="element-9">
                <p data-id="element-10">
                  Te levantas con el vientre plano, pero a las 7 de la tarde tus
                  pantalones favoritos te aprietan tanto que necesitas
                  desabrocharlos. No es falta de voluntad y no es solo "la
                  edad". Es tu cuerpo atrapado en un ciclo de estrés hormonal.
                </p>
                <p data-id="element-11">
                  Para una mujer de más de 35 años, las reglas del metabolismo
                  han cambiado. Lo que te funcionaba a los 20 ahora solo te deja
                  más cansada, más hambrienta y más inflamada. Es hora de dejar
                  de pelear contra tu biología y empezar a usarla a tu favor.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4" data-id="element-12">
                <a href="#offer" className="inline-flex justify-center items-center px-8 py-4 rounded-lg bg-sage-600 text-white font-medium hover:bg-sage-900 transition-colors duration-300 shadow-sm hover:shadow-md" data-id="element-13">
                  Obtener acceso instantáneo
                  <ArrowRight className="ml-2 w-4 h-4" data-id="element-14" />
                </a>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 1,
            delay: 0.2,
            ease: 'easeOut'
          }} className="relative lg:ml-auto" data-id="element-15">
              <div className="absolute inset-0 bg-sage-200/30 rounded-[2rem] transform rotate-3 scale-105 -z-10" data-id="element-16"></div>
              <img src={MOCKUP_URL} alt="Poder Perimenopáusico eBook en tablet" className="w-full max-w-lg mx-auto rounded-2xl shadow-2xl object-cover" data-id="element-17" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* THE PROBLEM */}
      <section className="py-24 bg-ivory-sec" data-id="element-18">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center" data-id="element-19">
          <FadeIn data-id="element-20">
            <h2 className="font-serif text-3xl sm:text-4xl mb-8" data-id="element-21">
              El laboratorio químico gobernado por el Cortisol
            </h2>
            <div className="text-lg text-sage-900/80 space-y-6 font-light leading-relaxed text-left sm:text-center" data-id="element-22">
              <p data-id="element-23">
                Después de los 38, tu cuerpo deja de ser una calculadora de
                calorías y se convierte en un laboratorio químico gobernado por
                el Cortisol. Cuando intentas comer menos o hacer más cardio, tu
                cuerpo entra en modo pánico.
              </p>
              <p data-id="element-24">
                Esa "hinchazón misteriosa" que aparece al final del día no es
                grasa acumulada; es{' '}
                <strong className="font-medium text-sage-900" data-id="element-25">
                  inflamación celular
                </strong>
                . Tu cuerpo está reteniendo líquido y almacenando energía en tu
                abdomen como un mecanismo de defensa contra el estrés de tu vida
                diaria y de las dietas que ya no funcionan.
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* WHY IT DIDN'T WORK */}
      <section className="py-24" data-id="element-26">
        <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-27">
          <FadeIn data-id="element-28">
            <div className="text-center max-w-2xl mx-auto mb-16" data-id="element-29">
              <h2 className="font-serif text-3xl sm:text-4xl mb-6" data-id="element-30">
                Por qué lo que intentaste no funcionó
              </h2>
              <p className="text-sage-900/70 font-light" data-id="element-31">
                Las reglas han cambiado, y las estrategias antiguas ahora juegan
                en tu contra.
              </p>
            </div>
          </FadeIn>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto" data-id="element-32">
            {[{
            title: 'Dietas Keto o ayuno extremo',
            desc: "Elevan tu cortisol, dándole a tu cuerpo la orden de 'ahorrar grasa abdominal' por supervivencia."
          }, {
            title: 'Conteo de calorías',
            desc: 'Ignora tus fluctuaciones de estrógeno, dejándote sin los bloques de construcción necesarios para tus hormonas.'
          }, {
            title: 'Cardio intenso',
            desc: 'A esta edad a menudo solo logra agotarte y disparar tus niveles de ansiedad por el azúcar.'
          }, {
            title: "Productos 'light'",
            desc: 'Están cargados de edulcorantes que confunden a tu intestino y empeoran la inflamación.'
          }].map((item, i) => <FadeIn key={i} delay={i * 0.1} data-id="element-33">
                <div className="bg-white p-8 rounded-2xl shadow-sm border border-sage-200/30 h-full" data-id="element-34">
                  <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center mb-4" data-id="element-35">
                    <X className="w-5 h-5 text-red-400" data-id="element-36" />
                  </div>
                  <h3 className="font-serif text-xl mb-3" data-id="element-37">{item.title}</h3>
                  <p className="text-sage-900/70 font-light leading-relaxed" data-id="element-38">
                    {item.desc}
                  </p>
                </div>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* UNIQUE MECHANISM */}
      <section className="py-24 bg-sage-900 text-ivory" data-id="element-39">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center" data-id="element-40">
          <FadeIn data-id="element-41">
            <Sparkles className="w-8 h-8 text-sage-200 mx-auto mb-6 opacity-80" data-id="element-42" />
            <h2 className="font-serif text-3xl sm:text-4xl mb-8" data-id="element-43">
              El Protocolo "Cortisol-Primero"
            </h2>
            <p className="text-lg sm:text-xl text-ivory/80 font-light leading-relaxed mb-8" data-id="element-44">
              En lugar de enfocarnos en cuánto comes, nos enfocamos en qué señal
              le envías a tu sistema nervioso. Al combinar 3 tipos específicos
              de ingredientes en el orden correcto, le dices a tu cerebro que el
              peligro ha pasado.
            </p>
            <p className="text-lg sm:text-xl text-ivory/80 font-light leading-relaxed" data-id="element-45">
              Cuando el cortisol baja, la inflamación desaparece y tu cuerpo
              finalmente se permite liberar la grasa retenida.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* WHAT YOU GET */}
      <section className="py-24 bg-ivory-sec" data-id="element-46">
        <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-47">
          <div className="grid lg:grid-cols-2 gap-16 items-center" data-id="element-48">
            <FadeIn data-id="element-49">
              <h2 className="font-serif text-3xl sm:text-4xl mb-10" data-id="element-50">
                Lo que obtienes hoy
              </h2>
              <div className="space-y-6" data-id="element-51">
                {["El eBook Digital completo de 'Poder Perimenopáusico'.", 'El Marco de los 3 Ingredientes: Cómo armar platos desinflamantes en menos de 15 minutos.', 'Tu Mapa de 5 Platos de Rescate para días de alto estrés.', "Lista de compra 'Anti-Hinchazón' para eliminar químicos inflamatorios.", 'Guía de Micro-hábitos de 2 minutos para apagar el interruptor del estrés después de comer.', 'Rastreador de Energía Diaria para identificar tus disparadores personales.'].map((item, i) => <div key={i} className="flex items-start gap-4" data-id="element-52">
                    <div className="mt-1 bg-sage-200/50 rounded-full p-1 shrink-0" data-id="element-53">
                      <Check className="w-4 h-4 text-sage-900" data-id="element-54" />
                    </div>
                    <p className="text-sage-900/80 font-light leading-relaxed" data-id="element-55">
                      {item}
                    </p>
                  </div>)}
              </div>
            </FadeIn>
            <FadeIn delay={0.2} data-id="element-56">
              <div className="relative" data-id="element-57">
                <div className="absolute inset-0 bg-white rounded-3xl transform -rotate-3 shadow-sm" data-id="element-58"></div>
                <img src={MOCKUP_URL} alt="Contenido del programa" className="relative rounded-3xl shadow-lg w-full object-cover" data-id="element-59" />
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* WHO IS THIS FOR / NOT FOR */}
      <section className="py-24" data-id="element-60">
        <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-61">
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto" data-id="element-62">
            <FadeIn data-id="element-63">
              <div className="bg-white p-10 rounded-3xl shadow-sm border border-sage-200/30 h-full" data-id="element-64">
                <h3 className="font-serif text-2xl mb-8 flex items-center gap-3" data-id="element-65">
                  <Check className="w-6 h-6 text-sage-600" data-id="element-66" />
                  Para quién es
                </h3>
                <ul className="space-y-6" data-id="element-67">
                  {['Mujeres de 35 a 55 años que notan que su cuerpo ya no responde a las dietas de siempre.', 'Madres y profesionales ocupadas que no tienen tiempo para cocinar recetas complejas.', 'Mujeres que sufren de fatiga mental (brain fog) y caídas de energía por la tarde.', 'Personas que buscan una solución práctica y basada en la ciencia hormonal, no en mitos de gimnasio.'].map((item, i) => <li key={i} className="flex items-start gap-3 text-sage-900/80 font-light leading-relaxed" data-id="element-68">
                      <span className="text-sage-600 mt-1.5" data-id="element-69">•</span>
                      {item}
                    </li>)}
                </ul>
              </div>
            </FadeIn>

            <FadeIn delay={0.2} data-id="element-70">
              <div className="bg-ivory-sec p-10 rounded-3xl h-full" data-id="element-71">
                <h3 className="font-serif text-2xl mb-8 flex items-center gap-3 opacity-80" data-id="element-72">
                  <X className="w-6 h-6 text-sage-900/50" data-id="element-73" />
                  Para quién NO es
                </h3>
                <ul className="space-y-6" data-id="element-74">
                  {["Quienes buscan una 'pastilla mágica' o resultados de la noche a la mañana sin cambiar sus hábitos básicos.", 'Personas que prefieren seguir contando calorías y pesando la comida obsesivamente.', 'Hombres o mujeres jóvenes cuyos sistemas hormonales funcionan de manera distinta.'].map((item, i) => <li key={i} className="flex items-start gap-3 text-sage-900/70 font-light leading-relaxed" data-id="element-75">
                      <span className="text-sage-900/40 mt-1.5" data-id="element-76">•</span>
                      {item}
                    </li>)}
                </ul>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-24 bg-sage-900 text-ivory" data-id="element-77">
        <div className="max-w-5xl mx-auto px-6 lg:px-8" data-id="element-78">
          <FadeIn data-id="element-79">
            <div className="text-center mb-16" data-id="element-80">
              <h2 className="font-serif text-3xl sm:text-4xl mb-4" data-id="element-81">
                Cómo funciona
              </h2>
              <p className="text-ivory/70 font-light" data-id="element-82">
                Un proceso simple de 3 pasos para recuperar tu equilibrio.
              </p>
            </div>
          </FadeIn>

          <div className="grid md:grid-cols-3 gap-8" data-id="element-83">
            {[{
            step: '1',
            title: 'Identifica',
            desc: 'Aprende a leer las señales de luz roja de tu abdomen antes de que la hinchazón aparezca.'
          }, {
            step: '2',
            title: 'Ensambla',
            desc: 'Aplica la regla de los 3 ingredientes en platos rápidos que puedes preparar en casa o elegir en un restaurante.'
          }, {
            step: '3',
            title: 'Estabiliza',
            desc: 'Usa los micro-hábitos para mantener tus hormonas en calma durante todo el día y recuperar tu sueño.'
          }].map((item, i) => <FadeIn key={i} delay={i * 0.1} data-id="element-84">
                <div className="text-center" data-id="element-85">
                  <div className="w-16 h-16 rounded-full bg-sage-600/30 flex items-center justify-center mx-auto mb-6 text-2xl font-serif text-sage-200" data-id="element-86">
                    {item.step}
                  </div>
                  <h3 className="font-serif text-xl mb-4" data-id="element-87">{item.title}</h3>
                  <p className="text-ivory/70 font-light leading-relaxed" data-id="element-88">
                    {item.desc}
                  </p>
                </div>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* PROOF */}
      <section className="py-24" data-id="element-89">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center" data-id="element-90">
          <FadeIn data-id="element-91">
            <Clock className="w-8 h-8 text-sage-600 mx-auto mb-6 opacity-80" data-id="element-92" />
            <h2 className="font-serif text-3xl sm:text-4xl mb-8" data-id="element-93">
              Lo que cambia en 7 días
            </h2>
            <p className="text-xl text-sage-900/80 font-light leading-relaxed" data-id="element-94">
              En la primera semana, notarás que la niebla mental desaparece, tu
              digestión se vuelve silenciosa y, lo más importante, llegarás a la
              noche con la misma talla de pantalón con la que empezaste el día.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-ivory-sec" data-id="element-95">
        <div className="max-w-3xl mx-auto px-6 lg:px-8" data-id="element-96">
          <FadeIn data-id="element-97">
            <h2 className="font-serif text-3xl sm:text-4xl mb-12 text-center" data-id="element-98">
              Preguntas Frecuentes
            </h2>
          </FadeIn>
          <div className="space-y-4" data-id="element-99">
            {[{
            q: '¿Tengo que dejar de comer carbohidratos?',
            a: 'No, aprenderás a comerlos en el orden correcto para que no disparen tu insulina.'
          }, {
            q: '¿Necesito suplementos caros?',
            a: 'No, la base es comida real, aunque recomendamos 3 minerales básicos opcionales.'
          }, {
            q: '¿Es apto para vegetarianas?',
            a: 'Sí, el protocolo se adapta fácilmente a fuentes de proteína vegetal.'
          }, {
            q: '¿Cuánto tiempo toma ver resultados?',
            a: 'La mayoría de las mujeres reportan una reducción notable de la hinchazón en los primeros 3 a 5 días.'
          }, {
            q: '¿Es difícil de seguir?',
            a: 'Es lo más sencillo del mundo: sin pesar comida, sin contar puntos, solo 3 ingredientes.'
          }].map((faq, i) => <FAQItem key={i} question={faq.q} answer={faq.a} delay={i * 0.1} data-id="element-100" />)}
          </div>
        </div>
      </section>

      {/* OFFER + CTA */}
      <section id="offer" className="py-24 lg:py-32" data-id="element-101">
        <div className="max-w-4xl mx-auto px-6 lg:px-8" data-id="element-102">
          <FadeIn data-id="element-103">
            <div className="bg-white rounded-[2.5rem] p-8 sm:p-12 lg:p-16 text-center shadow-xl shadow-sage-900/5 border border-sage-200/50" data-id="element-104">
              <ShieldCheck className="w-12 h-12 text-sage-600 mx-auto mb-6" data-id="element-105" />
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl mb-6" data-id="element-106">
                Recupera tu confianza y tu energía hoy mismo.
              </h2>
              <p className="text-lg sm:text-xl text-sage-900/80 font-light mb-10 max-w-2xl mx-auto" data-id="element-107">
                Por el precio de una cena rápida, obtén el sistema completo para
                sanar tu relación con tu cuerpo y tus hormonas.
              </p>

              <div className="mb-10" data-id="element-108">
                <span className="inline-block mb-4 py-1 px-4 rounded-full bg-sage-600 text-white text-xs font-semibold tracking-widest uppercase" data-id="element-109">
                  Oferta por tiempo limitado · 66% de descuento
                </span>
                <span className="text-sm font-medium tracking-widest uppercase text-sage-600 mb-2 block" data-id="element-110">
                  Acceso inmediato por solo
                </span>
                <div className="flex items-baseline justify-center gap-3" data-id="element-111">
                  <span className="font-serif text-2xl text-sage-900/40 line-through" data-id="element-112">
                    $19
                  </span>
                  <span className="font-serif text-6xl text-sage-900" data-id="element-113">
                    $6.39
                  </span>
                </div>
              </div>

              <a href="https://efectoraiz.com/cart/46110336680072:1" className="w-full sm:w-auto inline-flex justify-center items-center px-10 py-5 rounded-xl bg-sage-600 text-white text-lg font-medium hover:bg-sage-900 transition-all duration-300 shadow-lg shadow-sage-600/20 hover:shadow-xl hover:shadow-sage-900/20 hover:-translate-y-0.5" data-id="element-114">
                ¡SÍ, QUIERO MI COPIA AHORA!
              </a>
              <p className="mt-4 text-sm text-sage-900/50 font-light" data-id="element-115">
                Haz clic para obtener acceso instantáneo y empezar hoy mismo.
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-12 border-t border-sage-200/30 text-center" data-id="element-116">
        <div className="flex justify-center gap-6 mb-4" data-id="element-117">
          <button onClick={() => setActiveModal('privacy')} className="text-sm text-sage-900/60 font-light hover:text-sage-900 transition-colors" data-id="element-118">
            Política de Privacidad
          </button>
          <span className="text-sage-900/20" aria-hidden="true" data-id="element-119">
            ·
          </span>
          <button onClick={() => setActiveModal('terms')} className="text-sm text-sage-900/60 font-light hover:text-sage-900 transition-colors" data-id="element-120">
            Términos y Condiciones
          </button>
        </div>
        <p className="text-sm text-sage-900/40 font-light" data-id="element-121">
          © {new Date().getFullYear()} Poder Perimenopáusico. Todos los
          derechos reservados.
        </p>
      </footer>

      <LegalModal isOpen={activeModal !== null} onClose={() => setActiveModal(null)} title={activeModal === 'privacy' ? 'Política de Privacidad' : 'Términos y Condiciones'} data-id="element-122">
        {activeModal === 'privacy' ? <PrivacyContent data-id="element-123" /> : <TermsContent data-id="element-124" />}
      </LegalModal>
    </div>;
}
function LegalModal({
  isOpen,
  onClose,
  title,
  children
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) {
  return <AnimatePresence data-id="element-125">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.25
    }} onClick={onClose} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-sage-900/40 backdrop-blur-sm" role="dialog" aria-modal="true" aria-label={title} data-id="element-126">
          <motion.div initial={{
        opacity: 0,
        scale: 0.96,
        y: 16
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.96,
        y: 16
      }} transition={{
        duration: 0.3,
        ease: 'easeOut'
      }} onClick={e => e.stopPropagation()} className="relative w-full max-w-xl max-h-[85vh] flex flex-col bg-ivory rounded-3xl shadow-2xl overflow-hidden" data-id="element-127">
            <div className="flex items-center justify-between px-8 pt-8 pb-4 shrink-0" data-id="element-128">
              <h2 className="font-serif text-2xl text-sage-900" data-id="element-129">{title}</h2>
              <button onClick={onClose} aria-label="Cerrar" className="p-2 rounded-full text-sage-900/50 hover:text-sage-900 hover:bg-sage-200/40 transition-colors" data-id="element-130">
                <X className="w-5 h-5" data-id="element-131" />
              </button>
            </div>
            <div className="px-8 pb-8 overflow-y-auto text-sage-900/75 font-light leading-relaxed space-y-4 text-sm" data-id="element-132">
              {children}
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}
function PrivacyContent() {
  return <>
      <p data-id="element-133">
        Tu privacidad es importante para nosotros. Esta política explica cómo
        recopilamos, usamos y protegemos la información relacionada con tu
        compra del eBook "Poder Perimenopáusico".
      </p>
      <h3 className="font-serif text-base text-sage-900 pt-2" data-id="element-134">
        Información que recopilamos
      </h3>
      <p data-id="element-135">
        Al realizar tu compra recopilamos únicamente los datos necesarios para
        procesar el pago y entregarte el acceso digital al producto, como tu
        nombre y correo electrónico. No almacenamos los datos de tu tarjeta;
        estos son gestionados de forma segura por nuestro proveedor de pagos.
      </p>
      <h3 className="font-serif text-base text-sage-900 pt-2" data-id="element-136">
        Uso de la información
      </h3>
      <p data-id="element-137">
        Utilizamos tu correo electrónico exclusivamente para enviarte el acceso
        a tu eBook y comunicaciones directamente relacionadas con tu compra. No
        vendemos ni compartimos tu información con terceros con fines
        publicitarios.
      </p>
      <h3 className="font-serif text-base text-sage-900 pt-2" data-id="element-138">Tus derechos</h3>
      <p data-id="element-139">
        Puedes solicitar la revisión, actualización o eliminación de tus datos
        personales en cualquier momento. Tu información se conserva solo durante
        el tiempo necesario para cumplir con las finalidades aquí descritas.
      </p>
      <p data-id="element-140">
        Al completar tu compra, aceptas las prácticas descritas en esta Política
        de Privacidad.
      </p>
    </>;
}
function TermsContent() {
  return <>
      <p data-id="element-141">
        Al adquirir y utilizar el eBook "Poder Perimenopáusico" aceptas los
        siguientes términos y condiciones.
      </p>
      <h3 className="font-serif text-base text-sage-900 pt-2" data-id="element-142">
        Naturaleza del producto
      </h3>
      <p data-id="element-143">
        El producto es un material educativo digital de descarga inmediata. El
        contenido tiene fines exclusivamente informativos y no sustituye el
        consejo, diagnóstico o tratamiento de un profesional de la salud.
        Consulta siempre a tu médico antes de iniciar cualquier cambio en tu
        alimentación o estilo de vida.
      </p>
      <h3 className="font-serif text-base text-sage-900 pt-2" data-id="element-144">
        Licencia de uso
      </h3>
      <p data-id="element-145">
        Tu compra te otorga una licencia personal e intransferible para acceder
        al contenido. Queda prohibida la reproducción, distribución o reventa
        del material sin autorización por escrito.
      </p>
      <h3 className="font-serif text-base text-sage-900 pt-2" data-id="element-146">Resultados</h3>
      <p data-id="element-147">
        Los resultados pueden variar de una persona a otra. No garantizamos
        resultados específicos, ya que estos dependen de factores individuales y
        de la aplicación del protocolo.
      </p>
      <h3 className="font-serif text-base text-sage-900 pt-2" data-id="element-148">
        Acceso y reembolsos
      </h3>
      <p data-id="element-149">
        Por tratarse de un producto digital de acceso inmediato, las condiciones
        de reembolso se ajustan a la política vigente en el momento de la
        compra.
      </p>
    </>;
}
function FAQItem({
  question,
  answer,
  delay
}: {
  question: string;
  answer: string;
  delay: number;
}) {
  const [isOpen, setIsOpen] = useState(false);
  return <FadeIn delay={delay} data-id="element-150">
      <div className="bg-white rounded-2xl border border-sage-200/30 overflow-hidden transition-colors hover:border-sage-200/60" data-id="element-151">
        <button onClick={() => setIsOpen(!isOpen)} className="w-full px-6 py-5 text-left flex justify-between items-center gap-4" data-id="element-152">
          <span className="font-serif text-lg text-sage-900" data-id="element-153">{question}</span>
          <ChevronDown className={`w-5 h-5 text-sage-600 transition-transform duration-300 shrink-0 ${isOpen ? 'rotate-180' : ''}`} data-id="element-154" />
        </button>
        <AnimatePresence data-id="element-155">
          {isOpen && <motion.div initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.3,
          ease: 'easeInOut'
        }} data-id="element-156">
              <div className="px-6 pb-5 text-sage-900/70 font-light leading-relaxed" data-id="element-157">
                {answer}
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </FadeIn>;
}